
// Core Dependencies
import Vue from 'vue';
import Vuex from 'vuex';
Vue.use(Vuex);

// Vuex Tools and Plugins
import VuexPersistence from 'vuex-persist';
const vuexLocal = new VuexPersistence({storage: window.localStorage});

// Additional Libraries
import axios from 'axios';

// Additional Vue Dependencies
import router from '@/router/index';
import cookies from 'vue-cookies';

// Local Dependenciess
import OB from './onboard';
import Helper from './helper';
import Auth from './auth';
import Bundle from './bundle';
import Setpoints from './setpoints';
import Rest from './rest';
import Hover from './hover';
import API from './apiDocs';
import Thresh from '@/lib/threshParser';
import Generate from '@/lib/genThresh';
import Report from './report';

import Help from '@/lib/langTemplates/HelperClauses';

const baseUrl = 'https://3eunx8rcp7.execute-api.eu-west-1.amazonaws.com/dev/';

export default new Vuex.Store({

  state: {
    sessionEnd: -1,
    header: {},
    navNodes: [],

    buildingId: '',
    floorIndex: '',
    roomIndex: '',

    breakpoint: '',

    org: {},
    buildings: [],
    alerts: {},
    overallAlerts: {},
    activeBuilding: {},
    activeFloor: {},
    activeRoom: {},
    user: {},
    team: [],

    loading: false,
    lang: 'en',

    bubbles: true,

    cookieAccepted: false,
    backLabel: 'Daily'
  },

  getters: {

    getActiveBuilding: state => () => {
      return state.buildings.find(b => b.id === state.buildingId);
    },

    getActiveFloor: state => () => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      const foundParent = floor.rooms.find(room => room.parent);
      return floor;
    },

    getRoomsOfFloor: state => () => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      if (floor.rooms.some(r => r.parent)) {
        const trueParentIndex = floor.rooms.indexOf(floor.rooms.find(room => room.parent && !room.parent.info));
        arr.splice(trueParentIndex, 1);
        floor.rooms.unshift(trueParentIndex);
      }
      return floor.rooms;
    },

    getActiveRoom: state => () => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      return floor.rooms[parseInt(state.roomIndex)];
    },

    getFloorRoom: state => did => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      return floor.rooms.find(r => r.did);
    },

    getStatusOfRoom: state => did => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      const room = floor.rooms.find(r => r.did === did);
      let status = {};
      if (room.info.coilSensor) {
        ['bad', 'crit'].forEach(tier => {
          if (room.alerts.days[tier]) {
            console.log({ALERT_COIL_ROOM: room});
          }
        });
      }
      if (!room.meas || room.meas === 'no data' || Object.keys(room.meas).every(k => (!room.meas[k] || room.meas[k] === 'no data'))) {
        status.color = 'rgb(0, 153, 191)';
      } else if (room.overallAlerts.crit > 0) {
        status.color = 'red';
      } else if (room.overallAlerts.bad > 0) {
        status.color = '#E6A752';
      } else {
        status.color = '#71C076';
      }
      return status;
    },

    rdgStatus: state => did => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      const room = floor.rooms.find(r => r.did === did.did);
      return (!room.meas || (!room.meas[did.rdg] && room.meas[did.rdg] != 0)) ? {color:'rgb(0,153,191)', img: ''} : Thresh.parseThresh(room.alerts, did.rdg, room.meas[did.rdg] === 'no data');
    },

    getRoomAlerts: state => did => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      const room = floor.rooms.find(r => r.did === did);
      return Object.keys(room.alerts).reduce((a, i) => {
        if (i == 'days' && room.info.coilSensor && ['bad', 'crit'].some(tier => room.alerts.days[tier])) {
          return a;
        } else {
          if (room.alerts[i].crit > 0) {
            a.push({rdg: i, crit: true});
          } else if (room.alerts[i].bad > 0) {
            a.push({rdg: i, crit: false})
          }
          return a;
        }
      }, []);
    },

    getIndexOfRoom: state => did => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      const room = floor.rooms.find(r => r.did === did);
      return floor.rooms.indexOf(room);
    },

    getInLang: state => temp => {
      let lang = require(`@/lib/langTemplates/${temp}Template`);
      Object.keys(lang.clauses).forEach(i => {
        lang.clauses[i] = lang.clauses[i][state.lang];
      });
      return lang;
    },

    getRoomSetpoints: state => did => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      const room = floor.rooms.find(r => r.did === did);
      return room.setpoints ? room.setpoints : Generate;
    },

    getParentRoom: state => id => {
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      return floor.rooms.find(r => r.airVentId == id);
    }

  },

  mutations: {

    activateCookie(state, cook){
      state.cookieAccepted = cook;
    },

    startSession(state){
      state.sessionEnd = Math.round(Date.now()/1000) + 3600;
    },

    setHeader(state, header){
      state.navNodes.push(header.type);
      state.header = header;
    },

    setCurrentNav(state, nav){
      state.navNodes = nav;
    },

    setOrg(state, org){
      state.org = org;
    },

    setBuildings(state, buildings){
      const demo = buildings.find(b => b.info.name == 'Maison de Seaton');
      if (demo) console.log({ demo });
      state.buildings = buildings;
    },

    setBuildingId(state, id){
      state.roomId = '';
      state.floorIndex = '';
      state.buildingId = id;
    },

    setFloorIndex(state, index){
      state.roomIndex = '';
      state.floorIndex = index.toString();
    },

    setRoomIndex(state, index){
      state.roomIndex = index.toString();
    },

    addNewBuilding(state, bldgs){
      state.buildings = bldgs;
      ['buildingId', 'floorIndex', 'roomIndex'].forEach(i => state[i] = '');
      let header = {};
      header.type = 'org';
      header.title = state.org.name;
      header.lgText = state.buildings.length;
      header.smText = `Building${state.buildings.length === 1 ? '' : 's'}<br /> Monitored`;
      header.alerts = state.overallAlerts;
      const nav = ['org'];
      state.nav = nav;
      state.header = header;
      router.push({path: '/organization'});
    },

    addNewRoom(state, room){
      const breakpoint = room.breakpoint;
      delete room.breakpoint;
      let bldg = state.buildings.find(b => b.id === state.buildingId);
      const bIndex = state.buildings.indexOf(bldg);
      let floor;
      if (bldg.floors && bldg.floors.find(f => f.info.index == room.floorIndex)) {
        floor = bldg.floors.find(f => f.info.index == room.floorIndex);
      } else {
        floor = {
          info: {
            index: room.floorIndex,
            name: room.floorName ? room.floorName : `Floor ${room.floorIndex}`
          },
          rooms: []
        };
      }
      floor.rooms.push(room);
      let fIndex = 0;
      if (bldg.floors.length && bldg.floors.indexOf(floor) !== -1) {
        fIndex = bldg.floors.indexOf(floor);
        bldg.floors[fIndex] = floor;
      } else {
        bldg.floors.push(floor);
      }
      state.buildings[bIndex] = bldg;
      let header = {};
      header.type = 'floor';
      header.title = room.floorName ? room.floorName : `Floor ${room.floorIndex}`;
      header.lgText = '';
      header.smText = '';
      header.alerts = floor.alerts ? floor.alerts : {bad: 0, crit: 0};
      state.nav = ['org', 'bldg', 'floor'];
      state.header = header;
      state.floorIndex = room.floorIndex.toString();
      router.push({path: breakpoint !== 'sm' ? `/heatmap/${room.floorIndex}` : `/floor/${room.floorIndex}`});
    },

    addUserToTeam(state, user){
      state.team.push(user);
    },

    deleteRoomFromState(state, did){
      let bldg = state.buildings.find(b => b.id === state.buildingId);
      const bIndex = state.buildings.indexOf(bldg);
      let floor = bldg.floors.find(f => f.info.index.toString() === state.floorIndex);
      const fIndex = bldg.floors.indexOf(floor);
      floor.rooms = floor.rooms ? floor.rooms.filter(r => r.did !== did.did) : [];
      if (floor.rooms.length) {
        floor.alerts = aggAlerts(floor.rooms, 'floor');
        floor.meas = aggMeas(floor.rooms);
      } else {
        floor.alerts = new EmptyAlerts();
        floor.meas = {
          days: '-',
          ttp: '-',
          hhm: '-',
          iaq: '-',
          co2: '-'
        }
      }
      floor.overallAlerts = aggOverall(floor.alerts);
      bldg.floors[fIndex] = floor;
      bldg.alerts = aggAlerts(bldg.floors, 'bldg');
      bldg.overallAlerts = aggOverall(bldg.alerts);
      bldg.meas = aggMeas(bldg.floors);
      state.buildings[bIndex] = bldg;
      state.alerts = aggAlerts(state.buildings, 'org');
      state.overallAlerts = aggOverall(state.alerts);
      router.push({
        path:
          !floor.rooms.length
            ? `/building/${state.buildingId}`
            : (
              did.breakpoint !== 'sm'
                ? `/heatmap/${state.floorIndex}`
                : `/floor/${state.floorIndex}`
            )
      })
    },

    removeBldgFromState(state){
      state.buildings = state.buildings.filter(b => b.id !== state.buildingId);
      state.buildingId = '';
      state.alerts = aggAlerts(state.buildings);
      state.overallAlerts = aggOverall(state.alerts);
      state.header = {
        title: state.org.name,
        lgText: state.buildings.length,
        smText: `Building${state.buildings.length === 1 ? '' : 's'}<br/> Monitored`,
        alerts: state.overallAlerts,
        type: 'org'
      }
      state.nav = ['org'];
      if (!state.buildings.length) {
        state.onboard.obEntity = 'bldg';
        router.push({path: '/onboard'});
      } else {
        router.push({path: '/organization'});
      }
    },

    insertAsyncRoom(state, room){
      let bldg = state.buildings.find(b => b.id === state.buildingId);
      const bIndex = state.buildings.indexOf(bldg);
      let floor = bldg.floors.find(f => f.info.index == state.floorIndex);
      const fIndex = bldg.floors.indexOf(floor);
      const rIndex = floor.rooms.indexOf(floor.rooms.find(r => r.did === room.did));
      floor.rooms[floor.rooms.indexOf(floor.rooms.find(r => r.did === room.did))] = room;
      floor.alerts = aggAlerts(floor.rooms);
      floor.overallAlerts = aggOverall(floor.alerts);
      floor.meas = aggMeas(floor.rooms);
      bldg.floors[fIndex] = floor;
      bldg.alerts = aggAlerts(bldg.floors);
      bldg.overallAlerts = aggOverall(bldg.alerts);
      bldg.meas = aggMeas(bldg.floors);
      state.buildings[bIndex] = bldg;
      state.alerts = aggAlerts(state.buildings);
      state.overallAlerts = aggOverall(state.alerts);
    },

    updateAfterChangeFilter(state, vId){
      let bldg = state.buildings.find(b => b.id === state.buildingId);
      const bIndex = state.buildings.indexOf(bldg);
      let floor = bldg.floors.find(f => f.info.index == state.floorIndex);
      const fIndex = bldg.floors.indexOf(floor);
      let room = floor.rooms.find(r => r.airVentId === vId);
      const rIndex = floor.rooms.indexOf(room);
      room.meas.days = '-';
      room.alerts.days.crit = 0;
      room.alerts.days.bad = 0;
      room.lastChanged = Math.round(Date.now()/1000);
      room.overallAlerts = aggOverall(room.alerts);
      floor.rooms[rIndex] = room;
      floor.alerts = aggAlerts(floor.rooms);
      floor.overallAlerts = aggOverall(floor.alerts);
      floor.meas = aggMeas(floor.rooms);
      bldg.floors[fIndex] = floor;
      bldg.alerts = aggAlerts(bldg.floors);
      bldg.overallAlerts = aggOverall(bldg.alerts);
      bldg.meas = aggMeas(bldg.floors);
      state.buildings[bIndex] = bldg;
      state.alerts = aggAlerts(state.buildings);
      state.overallAlerts = aggOverall(state.alerts);
    },


    setAlerts(state, alerts){
      state.alerts = alerts.alerts;
      state.overallAlerts = alerts.overall;
    },

    setUser(state, user){
      if (user.lang) {
        state.lang = user.lang;
      }
      state.user = user;
    },

    setCurrentBldg(state, bldg){
      state.activeBuilding = bldg;
      state.activeFloor = {};
      state.activeRoom = {};
      router.push({path: '/building'});
    },

    setCurrentFloor(state, floor){
      state.activeFloor = floor.floor;
      state.activeRoom = {};
      router.push({path: floor.breakpoint === 'lg' ? '/heatmap' : '/floor'});
    },

    setCurrentRoom(state, room){
      // state.activeFloor.rooms = room.rooms
      state.activeRoom = room.room;
      router.push({path: room.breakpoint === 'sm' ? '/card' : '/room'});
    },

    setTeam(state, team){
      state.team = team;
    },

    updateUnits(state, pressure){
      const prop = pressure ? 'pressure' : 'metric';
      state.user[prop] = !state.user[prop];
    },

    updateHierarchy(state, hier){
      Object.keys(hier).forEach(i => state[i] = hier[i]);
      delete state.breakpoint;
      let path;
      let header = {};
      let nav;
      if (hier.activeRoom && hier.activeRoom.info) {
        header.type = 'room';
        header.title = hier.activeRoom.info.hvacName;
        header.lgText = '';
        header.smText = '';
        header.alerts = hier.activeRoom.overallAlerts;
        nav = ['org', 'bldg', 'floor', 'room']
        path = hier.breakpoint === 'lg' ? '/room' : '/card'
      } else if (hier.activeFloor && hier.activeFloor.info) {
        state.activeRoom = {};
        path = hier.breakpoint === 'lg' ? '/heatmap' : '/floor';
        header.type = 'floor';
        header.title = hier.activeFloor.info.name ? hier.activeFloor.info.name : `Floor ${hier.activeFloor.info.index}`;
        header.lgText = hier.acitveFloor.rooms.length;
        header.smText = `Room${hier.activeFloor.rooms.length === 1 ? '' : 's'}<br />Monitored`;
        header.alerts = hier.activeFloor.overallAlerts;
        nav = ['org', 'bldg', 'floor'];
      } else if (hier.activeBuilding && hier.activeBuilding.info) {
        state.activeRoom = {};
        state.activeFloor = {};
        header.type = 'building',
        header.title = hier.activeBuilding.info.name;
        header.lgText = hier.activeBuilding.floors.filter(f => f.rooms).length;
        header.smText = `of ${hier.activeBuilding.aboveGround + hier.activeBuilding.belowGround}<br />Floor${hier.activeBuilding.aboveGround + hier.activeBuilding.belowGround === 1 ? '' : 's'} Monitored`;
        header.alerts = hier.activeBuilding.overallAlerts;
        nav = ['org', 'bldg'];
        path = '/building';
      } else {
        state.activeRoom = {};
        state.activeFloor = {};
        state.activeBuilding = {};
        header.type = 'org';
        header.title = state.org.name;
        header.lgText = hier.buildings.length;
        header.smText = `Building${hier.buildings.length === 1 ? '' : 's'}<br /> Monitored`;
        header.alerts = hier.overallAlerts;
        nav = ['org'];
        path = '/organization';
      }
      state.header = header;
      state.navNodes = nav;
      router.push({path: path});
    },

    async resetHierarchy(state, floor){
      const url = `${baseUrl}bundle/${state.activeBuilding.id}`;
      const headers = {
        'Content-Type': 'application-json',
        Authorization: cookies.get('breeziProTkn')
      };
      try {
        const bldg = (await axios.get(url, {headers: headers})).data;
        const bIndex = state.buildings.find(b => b.id === bldg.id);
        state.activeBuilding = bldg;
        state.buildings[bIndex] = bldg;
        const activeFloor = state.activeBuilding.floors.find(f => f.info.index === floor);
        if (activeFloor.rooms && activeFloor.rooms.length) {
          state.activeFloor = activeFloor;
          if (router.currentRoute.path !== '/heatmap' && router.currentRoute.path !== '/floor') router.push({path: room.breakpoint === 'lg' ? '/heatmap' : '/floor'});
        } else {
          state.activeFloor = {};
          router.push({path: '/building'});
        }
      } catch(err) {
        throw 'reset err';
      }
    },

    resetState(state){
      state = Object.keys(state).reduce((a, i) => {
        if (typeof state[i] === 'Object') {
          a[i] = Object.keys(state[i]).reduce((x, y) => {
            y[x] = emptyVal(state[i][x]);
            return y;
          }, {});
        } else {
          return emptyVal(state[i]);
        }
        return a;
      }, {});
      // cookies.remove('breeziProTkn');
    },

    addThisUserToTeam(state){
      if (!state.team.find(u => u.email === state.user.email)) state.team.unshift({
        name: `${state.user.firstName} ${state.user.lastName}`,
        jobTitle: 'Me',
        email: state.user.email
      });
    },

    removeBldg(state, id){
      state.activeBuilding = {};
      state.buildings = state.buildings.filter(b => b.id !== id);
    },

    updateTeam(state, user){
      state.team.push(user);
    },

    clearEntities(state, tier){
      let entitiesForRemoval = [];
      switch (tier) {
        case 'floor':
          entitiesForRemoval = ['roomIndex'];
        break;
        case 'bldg':
          entitiesForRemoval = ['roomIndex', 'floorIndex'];
        break;
        case 'org':
          entitiesForRemoval = ['roomIndex', 'floorIndex', 'buildingId']
        break;
      }
      entitiesForRemoval.forEach(i => state[i] = '');
    },

    setDefaultSP(state, did){
      const bldg = state.buildings.find(b => b.id === state.buildingId);
      const bIndex = state.buildings.indexOf(bldg);
      const floor = bldg.floors.find(f => f.info.index.toString() == state.floorIndex);
      const fIndex = bldg.floors.indexOf(floor);
      const room = floor.rooms.find(r => r.did === did);
      const rIndex = floor.rooms.indexOf(room);
      room.setpoints = Generate;
      floor.rooms[rIndex] = room;
      bldg.floors[fIndex] = floor;
      state.buildings[bIndex] = bldg;
    },

    selectLanguage(state, lang){
      state.lang = lang;
    },

    togBubbles(state, bub){
      if (!bub) {
        state.bubbles = state.bubbles ? false : true;
      } else {
        state.bubbles = bub;
      }
    },

    toggleStateTutorial(state, tutorial){
      state.user.tutorial = tutorial;
    },

    applyBlockOrientation(state, block){
      state.changedOrientation = block;
    },

    dayTrendTmsFromRoom(state, tms){
      state.trendDayTms = tms;
    },

    setBackLabel(state, label){
      state.backLabel = label
    }

  },

  actions: {

    setCookieToState(context, cook){
      context.commit('activateCookie', cook);
    },

    clearState(context){
      context.commit('resetState')
    },

    setNav(context, nav){
      context.commit('setCurrentNav', nav);
    },

    initCurrentBldg(context, id){
      let bldg = context.state.buildings.find(b => b.id === id);
      let header = {};
      let roomCount = bldg.floors.reduce((a, i) => {
        if (i.rooms) {
          i.rooms.forEach(r => {
            if (
              !Object.keys(r.meas).every(x => r.meas[x] === 'no data')
            ) a ++;
          });
        }
        return a;
      }, 0);
      header.title = bldg.info.name;
      header.lgText = roomCount;
      header.smText = `of ${bldg.aboveGround + bldg.belowGround} Floor${bldg.aboveGround + bldg.belowGround === 1 ? '' : 's'} <br/> Monitored`;
      header.alerts = bldg.overallAlerts;
      header.type = 'bldg';
      context.commit('setBuildingId', id);
      router.push({path: `/building/${id}`});
    },

    setFloor(context, floorInfo){
      const building = context.state.buildings.find(b => b.id === context.state.buildingId);
      const floor = building.floors.find(f => f.info.index === floorInfo.index);
      if (floor && floor.rooms && floor.rooms.length && !floorInfo.navNew) {
        context.commit('setFloorIndex', floorInfo.index);
        let header = {
          title: floor.info && floor.info.name ? floor.info.name : `Floor ${floorInfo.index}`,
          lgText: `${floor.rooms.length}`,
          smText: `Room${floor.rooms.length === 1 ? '' : 's'} <br/> Monitored`,
          alerts: floor.overallAlerts,
          type: 'floor'
        }
        context.commit('setHeader', header);
        router.push({path: floorInfo.breakpoint !== 'sm' ? `/heatmap/${floorInfo.index}` : `/floor/${floorInfo.index}`});
      } else {
        context.commit('initNewRoom', floorInfo);
        // TODO: route NO ROOMS to ONBOARD!
      }
    },

    setRoom(context, roomInfo){
      const building = context.state.buildings.find(b => b.id === context.state.buildingId);
      const floor = building.floors.find(f => f.info.index.toString() == context.state.floorIndex);
      const room = floor.rooms.find(r => r.did === roomInfo.id);
      const rIndex = floor.rooms.indexOf(room);
      context.commit('setRoomIndex', rIndex);
      router.push({path: roomInfo.breakpoint === 'sm' ? `/card/${rIndex}` : `/room/${rIndex}`});
      // context.commit('setCurrentRoom', {room: room, breakpoint: roomInfo.breakpoint});
      let header = {
        title: room.info.hvacName,
        lgText: '',
        smText: '',
        alerts: room.overallAlerts,
        type: 'room'
      };
      context.commit('setHeader', header);
    },

    componentHeader(context, header){
      context.commit('setHeader', header);
    },

    async asyncFloorRoom(context, room){
      try {
        let floor = room.floorIndex;
        context.commit('resetHierarchy', floor);
      } catch(err) {
        console.log(err);
        let title = err === 'reset err' ? Help.clauses.langFail[`${context.state.lang}Title`] : Help.clauses.roomAddFail[`${context.state.lang}Title`];
        let body = Help.clauses.roomAddFail[context.state.lang];
        context.dispatch('initNotif', {
          type: 'err',
          title: title,
          body: body
        });
        if (err === 'reset err') router.push({path: '/'});
      }
    },

    addMeToTeam(context){
      context.commit('addThisUserToTeam');
    },

    logoutNotif(context){
      context.dispatch('initNotif', {
        type: 'err',
        title: Help.clauses.sessionExp[`${context.state.lang}Title`],
        body: Help.clauses.sessionExp[context.state.lang]
      });
    },

    updateDeleteRoom(context, did){
      let floor = context.state.activeFloor;
      floor.rooms = floor.rooms.filter(r => r.did !== did);
      context.commit('resetHierarchy', floor);
      if (!floor.rooms.length) context.commit('setCurrentBldg', context.state.activeBuilding);
    },

    clearIdsAndIndexes(context, tier){
      context.commit('clearEntities', tier);
    },

    setLang(context){
      context.commit('selectLanguage', context.state.lang === 'en' ? 'jp' : 'en');
    },

    async switchRestLang(context, lang){
      const url = `${baseUrl}language?lang=${lang}`;
      const headers = {
        'Content-Type': 'application-json',
        Authorization: cookies.get('breeziProTkn')
      };
      try {
        await axios.post(url, {}, {headers: headers});
        context.commit('selectLanguage', lang);
      } catch(err) {
        console.log({ERROR: err});
        context.dispatch('initNotif', {
          type: 'err',
          title: Help.clauses.langFail[`${context.state.lang}Title`],
          body: Help.clauses.langFail[context.state.lang]
        });
      }
    },

    switchBubbles(context, bub){
      context.commit('togBubbles', bub);
    },

    blockOrientation(context, block){
      context.commit('applyBlockOrientation', block);
    },

    setTrendDayTms(context, tms){
      context.commit('dayTrendTmsFromRoom', tms);
    },

    updateBackLabel(context, label){
      context.commit('setBackLabel', label);
    }

  },

  modules: {
    onboard: OB,
    helper: Helper,
    auth: Auth,
    bundle: Bundle,
    setpoints: Setpoints,
    rest: Rest,
    hover: Hover,
    apiDocs: API,
    report: Report
  },

  plugins: [vuexLocal.plugin]

});

function clearProp(k){
  if (Array.isArray[k]) {
    k = [];
  } else if (parseInt(k)) {
    k = -1;
  } else if (Object.keys(k).length) {
    k = {};
  } else {
    k = '';
  }
}

function emptyVal(v){
  if (Array.isArray(v)) {
    return [];
  } else {
    switch (typeof v) {
      case 'String':
        return '';
      break;
      case 'Number':
        return 0;
      break;
      case 'Boolean':
        return false;
      break;
    }
  }
}

function aggAlerts(arr, str){
  return arr.reduce((a, i) => {
    ['days', 'ttp', 'hhm', 'iaq', 'co2'].forEach(x => {
      if (i.alerts && i.alerts[x]) {
        a[x].bad += i.alerts[x].bad;
        a[x].crit += i.alerts[x].crit;
      }
    });
    return a;
  }, {
    days: {bad: 0, crit: 0},
    ttp: {bad: 0, crit: 0},
    hhm: {bad: 0, crit: 0},
    iaq: {bad: 0, crit: 0},
    co2: {bad: 0, crit: 0}
  })
}

function aggOverall(alerts){
  return Object.keys(alerts).reduce((a, i) => {
    if (alerts[i].crit) {
      a.crit += alerts[i].crit;
    } else if (alerts[i].bad) {
      a.bad += alerts[i].bad;
    }
    return a;
  }, {crit: 0, bad: 0});
}

function aggMeas(arr){
  return arr.reduce((a, i, x, r) => {
    ['days', 'ttp', 'hhm', 'iaq', 'co2'].forEach(n => {
      if (i.meas && i.meas[n] && parseInt(i.meas[n])) {
        a[n] = parseInt(a[n])
          ? a[n] + i.meas[n]
          : i.meas[n];
      }
    });
    if (x === r.length - 1) {
      ['days', 'ttp', 'hhm', 'iaq', 'co2'].forEach(n => {
        if (parseInt(a[n])) {
          a[n] = Math.round(a[n]/r.length);
        }
      })
    }
    return a;
  },{
    days: 'no data',
    ttp: 'no data',
    hhm: 'no data',
    iaq: 'no data',
    co2: 'no data'
  })
}

class EmptyAlerts {
  constructor(){
    const self = this;
    const rdgs = ['days', 'ttp', 'hhm', 'iaq', 'co2'];
    rdgs.forEach(i => self[i] = {bad: 0, crit: 0});
  }
}



// WEBPACK FOOTER //
// ./src/store/index.js